import { useState } from "react";
import { Ellipsis, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

import ACL_RELATIONSHIPS from "acl-relationships";
import { buttonVariants } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import Authorized from "components/Authorized";
import CaseDomainForm from "components/forms/CaseDomainForm";
import ConfirmDeleteDialog from "components/modals/ConfirmDeleteDialog";
import FormDialog from "components/modals/FormDialog";
import { invalidateCaseDomainsQuery } from "hooks/useCaseDomainsTable";
import CaseDomainService from "services/CaseDomain";

const EDIT_ACLS = [ACL_RELATIONSHIPS.caseDomains.edit];
const DELETE_ACLS = [ACL_RELATIONSHIPS.caseDomains.delete];
const ANY_ACLS = [...EDIT_ACLS, ...DELETE_ACLS];

export function CaseDomainsTableActions({ original }) {
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const handleDelete = async () => {
    try {
      await CaseDomainService.delete(original.id);
      toast.success("Case Domain deleted");
      setDeleteOpen(false);
      await invalidateCaseDomainsQuery();
    } catch {
      toast.error("Failed to delete Case Domain");
    }
  };

  return (
    <Authorized permissions={ANY_ACLS} partialMatch>
      <>
        <DropdownMenu>
          <DropdownMenuTrigger
            aria-label="Case domain actions"
            className={buttonVariants({ variant: "ghost", size: "icon-sm" })}
          >
            <Ellipsis />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <Authorized permissions={EDIT_ACLS}>
              <DropdownMenuItem onClick={() => setEditOpen(true)}>
                <Pencil />
                Edit
              </DropdownMenuItem>
            </Authorized>
            <Authorized permissions={DELETE_ACLS}>
              <DropdownMenuItem
                variant="destructive"
                onClick={() => setDeleteOpen(true)}
              >
                <Trash2 />
                Delete
              </DropdownMenuItem>
            </Authorized>
          </DropdownMenuContent>
        </DropdownMenu>
        <FormDialog
          FormComponent={CaseDomainForm}
          model={original}
          modelType="Case Domain"
          open={editOpen}
          onOpenChange={setEditOpen}
          onSuccess={invalidateCaseDomainsQuery}
        />
        <ConfirmDeleteDialog
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          title="Delete Case Domain?"
          description="Are you sure you want to delete this Case Domain?"
          onConfirm={handleDelete}
        />
      </>
    </Authorized>
  );
}
